import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";
import { COMPANY_TYPE_LABELS, COMPANY_TYPE_ORDER, FY_START_MONTHS } from "@/lib/companyTypes";

const EMPTY = {
  name: "",
  type: "private_limited",
  pan: "",
  gstin: "",
  tan: "",
  cin: "",
  registeredAddress: "",
  fyStartMonth: 4,
  incorporationDate: "",
  contactEmail: "",
  contactName: "",
  notes: "",
  isActive: true,
  hasGSTN: true,
  hasTAN: true,
  generateCompliances: true,
  generationMonths: 12,
};

export default function ClientFormModal({
  mode = "create",
  open,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    if (isEdit && initial) {
      setForm({
        ...EMPTY,
        name: initial.name || "",
        type: initial.type || "private_limited",
        pan: initial.pan || "",
        gstin: initial.gstin || "",
        contactEmail: initial.contactEmail || "",
        contactName: initial.contactName || "",
        notes: initial.notes || "",
        isActive: initial.isActive ?? true,
        hasGSTN: initial.hasGSTN ?? true,
        hasTAN: initial.hasTAN ?? true,
        generateCompliances: false,
      });
    } else {
      setForm(EMPTY);
    }
    setTouched({});
  }, [open, isEdit, initial]);

  const errors = useMemo(() => {
    const e = {};
    const n = form.name?.trim() || "";
    if (!n) e.name = "Client name is required";
    else if (n.length > 200) e.name = "Max 200 characters";
    if (form.contactEmail) {
      const em = form.contactEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.contactEmail = "Invalid email";
    }
    if (form.generateCompliances && !isEdit) {
      const m = Number(form.generationMonths);
      if (!Number.isInteger(m) || m < 1 || m > 36)
        e.generationMonths = "1–36 months";
    }
    return e;
  }, [form, isEdit]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const submit = (e) => {
    e?.preventDefault?.();
    setTouched({ name: true, contactEmail: true, generationMonths: true });
    if (!canSubmit) return;
    const payload = {
      name: form.name.trim(),
      type: form.type,
      pan: form.pan.trim(),
      gstin: form.gstin.trim(),
      contactEmail: form.contactEmail.trim(),
      contactName: form.contactName.trim(),
      notes: form.notes.trim(),
      isActive: form.isActive,
      hasGSTN: !!form.hasGSTN,
      hasTAN: !!form.hasTAN,
    };
    if (!isEdit) {
      payload.generateCompliances = form.generateCompliances;
      payload.generationMonths = Number(form.generationMonths);
    }
    onSubmit(payload);
  };

  const inputCls = (bad) =>
    cn(
      "w-full h-9 px-3 rounded-md border bg-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10",
      bad ? "border-[#DC2626]" : "border-[#E2E8F0]"
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 border-[#E2E8F0]"
        data-testid={TID.clientForm}
      >
        <form onSubmit={submit}>
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
            <DialogTitle className="text-base font-semibold text-[#0F172A]">
              {isEdit ? "Edit Client" : "New Client / Party"}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              {isEdit
                ? "Update client details. Existing tasks are preserved."
                : "Add a client and (optionally) generate their applicable statutory compliances."}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">
                Client / Party Name
              </span>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                maxLength={200}
                placeholder="e.g., Acme Textiles Pvt Ltd"
                className={inputCls(touched.name && errors.name)}
                data-testid={TID.clientFormName}
              />
              {touched.name && errors.name && (
                <span className="text-[11px] text-[#DC2626]">{errors.name}</span>
              )}
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">
                Company / Party Type
              </span>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger
                  className="h-9 border-[#E2E8F0]"
                  data-testid={TID.clientFormType}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPE_ORDER.map((t) => (
                    <SelectItem key={t} value={t}>
                      {COMPANY_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] text-[#94A3B8]">
                Determines the standard statutory compliance set that will be applied.
              </span>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">PAN</span>
                <input
                  value={form.pan}
                  onChange={(e) =>
                    setForm({ ...form, pan: e.target.value.toUpperCase() })
                  }
                  maxLength={10}
                  placeholder="AAAPL1234C"
                  className={inputCls(false)}
                  data-testid={TID.clientFormPan}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  GSTIN
                </span>
                <input
                  value={form.gstin}
                  onChange={(e) =>
                    setForm({ ...form, gstin: e.target.value.toUpperCase() })
                  }
                  maxLength={15}
                  placeholder="27AAAPL1234C1Z5"
                  className={inputCls(false)}
                  data-testid={TID.clientFormGstin}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">TAN</span>
                <input
                  value={form.tan}
                  onChange={(e) => setForm({ ...form, tan: e.target.value.toUpperCase() })}
                  maxLength={10}
                  placeholder="DELM12345B"
                  className={inputCls(false)}
                  data-testid="client-form-tan"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">CIN</span>
                <input
                  value={form.cin}
                  onChange={(e) => setForm({ ...form, cin: e.target.value.toUpperCase() })}
                  maxLength={21}
                  placeholder="U74110DL2020PTC123456"
                  className={inputCls(false)}
                  data-testid="client-form-cin"
                />
              </label>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">Registered Address</span>
              <textarea
                value={form.registeredAddress}
                onChange={(e) => setForm({ ...form, registeredAddress: e.target.value })}
                maxLength={500}
                rows={2}
                placeholder="Full registered office address"
                className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10"
                data-testid="client-form-address"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  Financial Year Start
                </span>
                <select
                  value={form.fyStartMonth}
                  onChange={(e) => setForm({ ...form, fyStartMonth: Number(e.target.value) })}
                  className="h-9 px-2 rounded-md border border-[#E2E8F0] bg-white text-sm"
                  data-testid="client-form-fy-start"
                >
                  {FY_START_MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  Incorporation Date
                </span>
                <input
                  type="date"
                  value={form.incorporationDate || ""}
                  onChange={(e) => setForm({ ...form, incorporationDate: e.target.value })}
                  className={inputCls(false)}
                  data-testid="client-form-incorporation-date"
                />
              </label>
            </div>

            <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">
                Registrations
              </div>
              <ToggleRow
                testId={TID.clientFormHasGstn}
                label="Has GSTN?"
                hint="Off skips all GST returns (GSTR-1/3B, CMP-08, ITC-04, GSTR-5/6/7/8)."
                checked={form.hasGSTN}
                onChange={(v) => setForm({ ...form, hasGSTN: v })}
              />
              <ToggleRow
                testId={TID.clientFormHasTan}
                label="Has TAN?"
                hint="Off skips all income-tax TDS/TCS filings (deposit, 24Q/26Q/27Q, 27EQ, 26QB)."
                checked={form.hasTAN}
                onChange={(v) => setForm({ ...form, hasTAN: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  Contact Person
                </span>
                <input
                  value={form.contactName}
                  onChange={(e) =>
                    setForm({ ...form, contactName: e.target.value })
                  }
                  maxLength={200}
                  placeholder="e.g., Rajesh Kumar"
                  className={inputCls(false)}
                  data-testid={TID.clientFormContact}
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  Contact Email
                </span>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) =>
                    setForm({ ...form, contactEmail: e.target.value })
                  }
                  onBlur={() =>
                    setTouched((t) => ({ ...t, contactEmail: true }))
                  }
                  placeholder="[email protected]"
                  className={inputCls(touched.contactEmail && errors.contactEmail)}
                  data-testid={TID.clientFormEmail}
                />
                {touched.contactEmail && errors.contactEmail && (
                  <span className="text-[11px] text-[#DC2626]">
                    {errors.contactEmail}
                  </span>
                )}
              </label>
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">Notes</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                maxLength={500}
                rows={2}
                placeholder="Anything worth remembering about this client…"
                className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] bg-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10"
              />
            </label>

            {!isEdit && (
              <div className="rounded-md border border-[#E2E8F0] p-3 bg-[#F8FAFC] flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-xs text-[#334155]">
                  <input
                    type="checkbox"
                    checked={form.generateCompliances}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        generateCompliances: e.target.checked,
                      })
                    }
                    className="h-3.5 w-3.5 accent-[#0F172A]"
                    data-testid={TID.clientFormGenerate}
                  />
                  <span className="font-medium">
                    Auto-generate applicable compliances
                  </span>
                </label>
                {form.generateCompliances && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]">
                      Horizon (months)
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={36}
                      value={form.generationMonths}
                      onChange={(e) =>
                        setForm({ ...form, generationMonths: e.target.value })
                      }
                      className={cn(
                        "w-20 h-8 px-2 rounded-md border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10",
                        touched.generationMonths && errors.generationMonths
                          ? "border-[#DC2626]"
                          : "border-[#E2E8F0]"
                      )}
                      data-testid={TID.clientFormMonths}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] rounded-b-lg">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9] disabled:opacity-50"
              data-testid={TID.clientFormCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-2"
              data-testid={TID.clientFormSubmit}
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit ? "Save Changes" : "Create Client"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ testId, label, hint, checked, onChange }) {
  return (
    <label className="flex items-start justify-between gap-3 cursor-pointer">
      <div className="min-w-0">
        <div className="text-xs font-medium text-[#0F172A]">{label}</div>
        <div className="text-[10px] text-[#64748B] leading-snug mt-0.5">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={cn(
          "shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors mt-0.5",
          checked ? "bg-[#0F172A]" : "bg-[#CBD5E1]"
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5"
          )}
        />
      </button>
    </label>
  );
}
